import { ReactNode } from 'react';

import { getStates, STATES_OF_USA } from '@utils/states';

import { AutocompleteOption } from '@interfaces';

import UniversityIcon from '@images/ScholarshipsIcons/university.svg?react';
import PrivateAwardIcon from '@images/ScholarshipsIcons/private-award.svg?react';
import GavelIcon from '@images/ScholarshipsIcons/gavel.svg?react';
import FreshmanIcon from '@images/ScholarshipsIcons/architecture.svg?react';
import GraduateIcon from '@images/ScholarshipsIcons/cake.svg?react';
import JuniorIcon from '@images/ScholarshipsIcons/emoji-emotions.svg?react';
import PostgraduateIcon from '@images/ScholarshipsIcons/school.svg?react';
import SeniorIcon from '@images/ScholarshipsIcons/emoji-events.svg?react';
import SophomoreIcon from '@images/ScholarshipsIcons/science.svg?react';
import BookIcon from '@images/ScholarshipsIcons/auto_stories.svg?react';
import EmojiSymbolsIcon from '@images/ScholarshipsIcons/emoji-symbols.svg?react';
import TradeSchoolIcon from '@images/ScholarshipsIcons/construction.svg?react';
import HearingIcon from '@images/ScholarshipsIcons/hearing.svg?react';
import EyeIcon from '@images/ScholarshipsIcons/eye.svg?react';
import PhysicalDisabilityIcon from '@images/ScholarshipsIcons/wheelchair-pickup.svg?react';
import RedeemIcon from '@images/ScholarshipsIcons/redeem.svg?react';
import SavingsIcon from '@images/ScholarshipsIcons/savings.svg?react';
import BedIcon from '@images/ScholarshipsIcons/king_bed.svg?react';
import ClassIcon from '@images/ScholarshipsIcons/class.svg?react';
import ExploreIcon from '@images/ScholarshipsIcons/travel_explore.svg?react';
import PaymentsIcon from '@images/ScholarshipsIcons/payments.svg?react';
import SupportIcon from '@images/ScholarshipsIcons/support.svg?react';
import StarIcon from '@images/ScholarshipsIcons/star_rate.svg?react';
import EmojiPeopleIcon from '@images/ScholarshipsIcons/emoji_people.svg?react';
import LeaderboardIcon from '@images/ScholarshipsIcons/leaderboard.svg?react';
import AccessibilityIcon from '@images/ScholarshipsIcons/accessibility.svg?react';
import TransgenderIcon from '@images/ScholarshipsIcons/transgender.svg?react';
import VolunteerIcon from '@images/ScholarshipsIcons/volunteer_activism.svg?react';
import ChurchIcon from '@images/ScholarshipsIcons/church.svg?react';
import HomeIcon from '@images/ScholarshipsIcons/home_work.svg?react';
import PoliceIcon from '@images/ScholarshipsIcons/local_police.svg?react';
import SingleParentIcon from '@images/ScholarshipsIcons/single_parent.svg?react';
import VerifiedIcon from '@images/ScholarshipsIcons/verified.svg?react';
import ImmigrantIcon from '@images/ScholarshipsIcons/immigrant.svg?react';
import MilitaryIcon from '@images/ScholarshipsIcons/military-service.svg?react';

const STATES_OF_CANADA = {
  AB: 'Alberta',
  NB: 'New Brunswick',
  ON: 'Ontario',
  QC: 'Quebec',
  NT: 'Northwest Territories',
  NS: 'Nova Scotia',
  NU: 'Nunavut',
  BC: 'British Columbia',
  YT: 'Yukon',
  MB: 'Manitoba',
  NF: 'Newfoundland',
  PE: 'Prince Edward Island',
  NL: 'Newfoundland and Labrador',
  SK: 'Saskatchewan',
};

export const STATES = getStates().states.reduce(
  (acc, state) => {
    return {
      ...acc,
      [state.value]: state.label,
    };
  },
  { ...STATES_OF_CANADA } as { [key: string]: string },
);

const STATES_OF_CANADA_OPTIONS = Object.entries(STATES_OF_CANADA).reduce((acc, [key, value]) => {
  return [...acc, { label: value, value: key }];
}, [] as AutocompleteOption[]);

export const ALL_STATES_OPTIONS = [
  ...STATES_OF_USA,
  { value: ' ', label: ' ', isDisabled: true },
  ...STATES_OF_CANADA_OPTIONS,
];

export const ETHNICITY_LABELS: { [key: string]: string } = {
  americanIndianAlaskaNative: 'American Indian / Alaska native',
  asianPacificIslander: 'Asian / Pacific Islander',
  blackNonHispanic: 'Black, non-Hispanic',
  hispanic: 'Hispanic',
};

export const APPLICATION_REQUIREMENTS_LABELS: { [key: string]: string } = {
  applicationForm: 'Application form',
  essay: 'Essay',
  portfolio: 'Portfolio',
  testScores: 'Test scores',
  autoBiography: 'Autobiography',
  financialAnalysis: 'Financial need analysis',
  recommendations: 'Recommendations or references',
  transcript: 'Transcript',
  communityService: 'Community service records',
  interview: 'Interview',
  resume: 'Resume',
  form214: 'DD form 214',
  driversLicense: 'Driver’s licence',
  photograph: 'Photograph',
  addressedEnvelope: 'A self-addresses stamped envelope with application',
  other: 'Other',
};

export const MILITARY_LABELS: { [key: string]: string } = {
  anyService: 'Any military service',
  army: 'Army',
  navy: 'Navy',
  airNationalGuard: 'Air National Guard',
  airForce: 'Air Force ',
  marines: 'Marines',
  coastGuard: 'Coast Guard',
  armyNationalGuard: 'Army National Guard',
  specialRequirements: 'Special Requirements',
};

export const CLASSIFICATION_DATA: { [key: string]: { icon: ReactNode; label: string } } = {
  isGovernmentAid: {
    icon: <GavelIcon />,
    label: 'Government aid',
  },
  isInstitutionalAid: {
    icon: <UniversityIcon />,
    label: 'Institutional aid',
  },
  isPrivateAid: {
    icon: <PrivateAwardIcon />,
    label: 'Private aid',
  },
};

export const LEVEL_OF_STUDY_DATA: { [key: string]: { icon: ReactNode; label: string } } = {
  freshman: {
    icon: <FreshmanIcon />,
    label: 'Freshman',
  },
  graduate: {
    icon: <GraduateIcon />,
    label: 'First professional degree',
  },
  junior: {
    icon: <JuniorIcon />,
    label: 'Junior',
  },
  postGraduate: {
    icon: <PostgraduateIcon />,
    label: 'Postgraduate',
  },
  senior: {
    icon: <SeniorIcon />,
    label: 'Senior',
  },
  sophomore: {
    icon: <SophomoreIcon />,
    label: 'Sophomore',
  },
};

export const INSTITUTION_TYPE_DATA: { [key: string]: { icon: ReactNode; label: string } } = {
  fourYearCollege: {
    icon: <EmojiSymbolsIcon />,
    label: 'Four-year college',
  },
  tradeSchool: {
    icon: <TradeSchoolIcon />,
    label: 'Trade / Technical school',
  },
  twoYearCollege: {
    icon: <BookIcon />,
    label: 'Two-year college',
  },
  university: {
    icon: <UniversityIcon />,
    label: 'University',
  },
};

export const DISABILITY_DATA: { [key: string]: { icon: ReactNode; label: string } } = {
  hearing: {
    icon: <HearingIcon />,
    label: 'Hearing',
  },
  learning: {
    icon: <BookIcon />,
    label: 'Learning',
  },
  physical: {
    icon: <PhysicalDisabilityIcon />,
    label: 'Physical',
  },
  visual: {
    icon: <EyeIcon />,
    label: 'Visual',
  },
};

export const AWARD_INCLUSIONS_DATA: { [key: string]: { icon: ReactNode; label: string } } = {
  tuitionPayment: {
    icon: <RedeemIcon />,
    label: 'Tuition payment',
  },
  schoolFees: {
    icon: <SavingsIcon />,
    label: 'Payment of school fees',
  },
  roomAndBoard: {
    icon: <BedIcon />,
    label: 'Room and board',
  },
  booksAndSupplies: {
    icon: <ClassIcon />,
    label: 'Books and supplies',
  },
  researchFunding: {
    icon: <ExploreIcon />,
    label: 'Research funding',
  },
  cashPrize: {
    icon: <PaymentsIcon />,
    label: 'Cash prize',
  },
};

export const AWARD_CATEGORIES_DATA: { [key: string]: { icon: ReactNode; label: string } } = {
  needBased: {
    icon: <SupportIcon />,
    label: 'Need-based program',
  },
  meritBased: {
    icon: <StarIcon />,
    label: 'Merit-based program',
  },
  adult: {
    icon: <EmojiPeopleIcon />,
    label: 'Adult learner program',
  },
  athletic: {
    icon: <LeaderboardIcon />,
    label: 'Athletic program',
  },
  disability: {
    icon: <AccessibilityIcon />,
    label: 'Disability program',
  },
  lgbtq: {
    icon: <TransgenderIcon />,
    label: 'LGBTQ program',
  },
  military: {
    icon: <MilitaryIcon />,
    label: 'Military program',
  },
  minorities: {
    icon: <VolunteerIcon />,
    label: 'Minority program',
  },
  religious: {
    icon: <ChurchIcon />,
    label: 'Religious program',
  },
  residency: {
    icon: <HomeIcon />,
    label: 'State residency program ',
  },
  rotc: {
    icon: <PoliceIcon />,
    label: 'ROTC program',
  },
  singleParent: {
    icon: <SingleParentIcon />,
    label: 'Single parent program',
  },
  firstGeneration: {
    icon: <VerifiedIcon />,
    label: 'First generation',
  },
  immigrant: {
    icon: <ImmigrantIcon />,
    label: 'Immigrant',
  },
};
